import React from "react";
import formatDate from "../../../utils/formatDate";
import Button from "../../../components/Button/Button";
import {
  FaBell,
  FaCheckCircle,
  FaCoins,
  FaCreditCard,
  FaFileInvoice,
} from "react-icons/fa";
import { toAbsoluteArticlePhotoUrl } from "../../../services/articlesPhotoServices";

function hasEcheanceTag(message = "") {
  const clean = String(message)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  return clean.includes("echeance");
}

function canGenerateInvoice(article) {
  const isBoutiqueArticle =
    article?.article_id !== null && article?.article_id !== undefined;

  return isBoutiqueArticle && article?.statut_reglement === "Réglé";
}

function getArticleName(article) {
  if (article?.article_id === null) {
    return "Règlement Licence-Cotisation";
  }

  return article?.Article?.nom || article?.nomArticle || "Nom non disponible";
}

const ArticleCard = ({
  articles,
  getStatusColor,
  defaultPhotoUrl,
  handlePayerClick,
  handlePayerEnPlusieursFoisClick,
  canUseInstallmentPayments = false,
  payingId,
  handleNotifyClick,
  notifyingId,
  isAlreadyNotified,
  handleGenerateInvoice,
}) => {
  const FALLBACK = "/img/no-image.jpg";

  return (
    <div className="sm:hidden">
      {articles.length > 0 ? (
        articles.map((article) => {
          const isDisabled = article.autorisation_achat === false;

          const canShowButtons = ["Non Réglé", "En Attente"].includes(
            article.statut_reglement,
          );

          const rawPhoto =
            article?.Article?.photo_url || article?.photoUrl || defaultPhotoUrl;

          const imgSrc = rawPhoto
            ? toAbsoluteArticlePhotoUrl(rawPhoto)
            : FALLBACK;

          const articleName = getArticleName(article);

          const isInstallment = hasEcheanceTag(article.message_achat);

          const canShowInstallmentButton =
            canUseInstallmentPayments &&
            article.article_id === null &&
            !isInstallment;

          const alreadyNotified = isAlreadyNotified?.(article.id);

          const showInvoice = canGenerateInvoice(article);

          return (
            <div
              key={article.id}
              className="mb-4 flex flex-col rounded-lg bg-white p-4 shadow-md dark:bg-slate-200"
            >
              <div className="mb-4 flex w-full justify-center">
                <img
                  src={imgSrc}
                  alt={articleName}
                  className="h-32 w-auto rounded-lg object-cover"
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = FALLBACK;
                  }}
                />
              </div>

              <div className="flex-1">
                <div className="text-sm text-gray-500">
                  {formatDate(article.date_achat)}
                </div>

                <div className="text-sm font-semibold text-gray-500">
                  {article.quantite} x {articleName}
                </div>

                {article.message_achat && (
                  <div className="text-xs italic text-gray-800">
                    {article.message_achat}
                  </div>
                )}

                <div className="text-normal font-bold text-gray-500">
                  {article.prix_total} €
                </div>

                <span
                  className={`my-2 inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(
                    article.statut_reglement,
                  )}`}
                >
                  {article.statut_reglement}
                </span>

                {canShowButtons && (
                  <div className="mt-2 flex flex-col gap-2">
                    {isDisabled && (
                      <span className="text-xs italic text-red-700">
                        Paiement non activé
                      </span>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        text={
                          payingId === article.id ? "Redirection…" : "Payer"
                        }
                        icon={FaCreditCard}
                        onClick={
                          isDisabled
                            ? undefined
                            : () => handlePayerClick(article.id)
                        }
                        disabled={isDisabled || payingId === article.id}
                        className={`gap-2 ${
                          isDisabled ? "cursor-not-allowed opacity-40" : ""
                        }`}
                      />

                      {canShowInstallmentButton && (
                        <Button
                          text="Payer en plusieurs fois"
                          icon={FaCoins}
                          onClick={
                            isDisabled
                              ? undefined
                              : () =>
                                  handlePayerEnPlusieursFoisClick(article.id)
                          }
                          disabled={isDisabled}
                          className={`gap-2 whitespace-nowrap ${
                            isDisabled ? "cursor-not-allowed opacity-40" : ""
                          }`}
                        />
                      )}
                    </div>
                  </div>
                )}

                {article.statut_reglement === "Réglé" && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {showInvoice && (
                      <Button
                        text="Facture PDF"
                        icon={FaFileInvoice}
                        onClick={() => handleGenerateInvoice?.(article)}
                        className="gap-2"
                      />
                    )}

                    {alreadyNotified ? (
                      <span className="inline-flex items-center gap-2 rounded-full bg-green-100 px-3 py-2 text-xs font-semibold text-green-700">
                        <FaCheckCircle />
                        Admin prévenu
                      </span>
                    ) : (
                      <Button
                        text={
                          notifyingId === article.id
                            ? "Envoi…"
                            : "Prévenir l'admin"
                        }
                        icon={FaBell}
                        onClick={() => handleNotifyClick?.(article)}
                        disabled={notifyingId === article.id}
                        className="gap-2"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })
      ) : (
        <div className="px-4 py-4 text-center dark:text-black">
          Pas d'articles commandés
        </div>
      )}
    </div>
  );
};

export default ArticleCard;
